import { browser } from "$app/environment"
import { TastyLDClient } from "$lib/launchdarkly"
import { twApiClient } from "$lib/tastyworks-rest"
import session from "$lib/tastyworks-rest/session"
import { AO2_AUTH_SESSION_COOKIE_KEY } from "@tastyworks/tastyworks-api"
import { CROSS_DOMAIN_COOKIE_DESER } from "@tastyworks/tastyworks-api/session"
import Cookie from "js-cookie"
import { isNil } from "lodash-es"
import { locale, waitLocale } from "svelte-i18n"

import "$lib/i18n" // Import to initialize. Important

import { localStorageStore } from "$lib"
import { ErrorController } from "$lib/controller/error"
import { getIsMobile, getIsTastyWebView } from "$lib/util"
import * as Sentry from "@sentry/sveltekit"
import type { Alpha3Code } from "i18n-iso-countries/index"

const errorController = new ErrorController()

export const load = async () => {
  console.log(`account opening v${__APP_VERSION__}`)
  if (browser) {
    locale.set(window.navigator.language)
  }
  await waitLocale()

  setSessionTokenFromCrossDomainCookie()

  let user = null
  let client: TastyLDClient

  if (browser && session.sessionToken) {
    const response = await twApiClient.sessionService.validate(
      session.sessionToken
    )
    // 401 returns null data but no error
    if (response.isError || !response.hasData()) {
      session.clear()
    }
    user = response.data
  }

  if (user) {
    session.user = user
    session.currentEmail = user.email
    Sentry.setUser({ email: user.email })
    // Set LaunchDarkly context
    client = new TastyLDClient({
      kind: "user",
      key: user?.externalId,
      email: user?.email,
      username: user?.username,
      isEmployeeDomain: session.isEmployeeDomain,
      isNewUser: session.isNewUser,
      isMobile: getIsMobile(),
    })
  } else {
    client = new TastyLDClient()
    Sentry.setUser(null)
  }

  await client.initialize()
  return {
    user,
    client,
    featureFlags: client.flags,
    trackLaunchDarklyEvent: client.client.track,
    flushLD: client.client.flush,
    countryOfResidence: getCountryOfResidence(),
    isTastyWebView: getIsTastyWebView(),
    errorController,
  }
}

export const ssr = false
export const prerender = false

function setSessionTokenFromCrossDomainCookie() {
  const authCookie = Cookie.get(AO2_AUTH_SESSION_COOKIE_KEY)

  if (!isNil(authCookie)) {
    const payload = CROSS_DOMAIN_COOKIE_DESER.parse(authCookie)
    session.sessionToken = payload.authenticated.data.sessionToken
    Cookie.remove(AO2_AUTH_SESSION_COOKIE_KEY, {
      domain: import.meta.env.VITE_SESSION_DOMAIN,
    })
  }
}

function getCountryOfResidence() {
  return localStorageStore<Alpha3Code | "">("country-of-residence", "")
}
